/**
 * Rétablit les accents des libellés qui arrivent du serveur.
 *
 * Pourquoi ici et pas à la source : dans `server/src/domain/sectors.ts`, le
 * libellé d'un sous-secteur sert **aussi de requête Google** par défaut
 * (`sector(id, label, query = label.toLowerCase())`). Corriger les accents
 * là-bas changerait donc ce qui est réellement cherché chez Google, et par
 * ricochet les clés du cache de recherche. C'est un risque métier pour un
 * problème d'affichage : la correction se fait donc à l'affichage.
 *
 * Le dictionnaire porte sur les **mots**, pas sur les libellés entiers : un
 * nouveau sous-secteur composé du même vocabulaire (« Entretien espaces
 * verts », « Institut de beauté »…) est couvert sans rien ajouter.
 */

const WORDS: Record<string, string> = {
  // Catégories
  batiment: 'Bâtiment',
  beaute: 'Beauté',
  'bien-etre': 'Bien-être',
  hebergement: 'Hébergement',
  metiers: 'Métiers',
  sante: 'Santé',
  // Sous-secteurs
  cafe: 'Café',
  controle: 'Contrôle',
  demenageur: 'Déménageur',
  electricien: 'Électricien',
  epicerie: 'Épicerie',
  hotel: 'Hôtel',
  kinesitherapeute: 'Kinésithérapeute',
  macon: 'Maçon',
  osteopathe: 'Ostéopathe',
  patisserie: 'Pâtisserie',
  veterinaire: 'Vétérinaire',
  immobiliere: 'immobilière',
};

/** Conserve la casse d'origine : « beaute » en fin de libellé reste minuscule. */
function applyCase(source: string, accented: string): string {
  const head = accented.slice(0, 1);
  const tail = accented.slice(1);
  const startsUpper = source.slice(0, 1) === source.slice(0, 1).toUpperCase();
  return (startsUpper ? head.toUpperCase() : head.toLowerCase()) + tail;
}

export function accentuate(label: string): string {
  // On découpe en gardant les séparateurs, pour reconstruire à l'identique.
  return label.replace(/[\p{L}-]+/gu, (word) => {
    const replacement = WORDS[word.toLowerCase()];
    return replacement === undefined ? word : applyCase(word, replacement);
  });
}

/**
 * Libellés des cinq compteurs de quota.
 *
 * `src/lib/quota.ts` les écrit sans accents (« Recherche ciblee ») et son
 * ordre de déclaration sert de départage entre compteurs à égalité — on ne
 * touche donc pas à ce fichier, on ne fait que réhabiller ses clés.
 */
export const QUOTA_LABELS: Record<string, string> = {
  'text-search': 'Recherche ciblée',
  nearby: 'Toutes catégories',
  details: 'Fiches actualisées',
  geocoding: 'Adresses',
  pagespeed: 'Analyses de site',
};

export function quotaLabel(key: string, fallback: string): string {
  return QUOTA_LABELS[key] ?? fallback;
}
